import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Linkedin, Youtube, Phone, MapPin, Clock } from "lucide-react";
import Logo from "@/components/Logo";
import { useLang } from "@/contexts/LanguageContext";
import { SITE, NAV, SERVICES } from "@/data/site";
import { SERVICE_AREAS } from "@/data/serviceAreas";

// X (Twitter) and TikTok icons (simple inline SVG, lucide doesn't ship X/TikTok)
const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M18.244 2H21l-6.52 7.45L22.5 22h-6.84l-4.78-6.27L5.2 22H2.44l7-8L1.5 2h6.99l4.32 5.71L18.244 2Zm-1.2 18h1.62L7.06 4H5.34l11.7 16Z" />
  </svg>
);

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.5 6.6a6.3 6.3 0 0 1-3.7-1.2V15a5.2 5.2 0 1 1-5.2-5.2c.3 0 .6 0 .9.1v2.5a2.7 2.7 0 1 0 1.8 2.6V2h2.4a3.9 3.9 0 0 0 3.9 3.9v.7Z" />
  </svg>
);

const Footer = () => {
  const { t, lang } = useLang();

  const hourLabel = (k) => {
    if (k === "monFri") return t("common.monFri");
    if (k === "sat") return t("common.sat");
    if (k === "sun") return t("common.sun");
    return k;
  };

  const hourTime = (h) =>
    h.time === "closedOnlineOnly" ? t("common.closedOnlineOnly") : h.time;

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#082133] text-[#FAFAFA] mt-24 sm:mt-32 rounded-t-[40px] overflow-hidden grain"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10">
          {/* Brand block */}
          <div className="lg:col-span-4">
            <div className="inline-flex items-center gap-3">
              <span className="relative flex h-10 w-10 items-center justify-center rounded-2xl bg-[#FAFAFA] text-[#082133] font-display font-bold text-xl">
                G
                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-[#082133] ring-2 ring-[#FAFAFA]" />
              </span>
              <div className="leading-tight">
                <div className="font-display font-semibold text-xl">Genesis</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#FAFAFA]/60">
                  Appliance Repair
                </div>
              </div>
            </div>

            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-[#FAFAFA]/75">
              {t("footer.tagline")}
            </p>

            <div className="mt-8 flex items-center gap-3">
              <a
                href={SITE.social.facebook}
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                data-testid="footer-social-facebook"
                className="h-11 w-11 inline-flex items-center justify-center rounded-full border border-[#FAFAFA]/15 hover:bg-[#FAFAFA] hover:text-[#082133] transition"
              >
                <Facebook className="h-4 w-4" />
              </a>
              <a
                href={SITE.social.linkedin}
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                data-testid="footer-social-linkedin"
                className="h-11 w-11 inline-flex items-center justify-center rounded-full border border-[#FAFAFA]/15 hover:bg-[#FAFAFA] hover:text-[#082133] transition"
              >
                <Linkedin className="h-4 w-4" />
              </a>
              <a
                href={SITE.social.twitter}
                target="_blank"
                rel="noreferrer"
                aria-label="X (Twitter)"
                data-testid="footer-social-x"
                className="h-11 w-11 inline-flex items-center justify-center rounded-full border border-[#FAFAFA]/15 hover:bg-[#FAFAFA] hover:text-[#082133] transition"
              >
                <XIcon className="h-3.5 w-3.5" />
              </a>
              <a
                href={SITE.social.tiktok}
                target="_blank"
                rel="noreferrer"
                aria-label="TikTok"
                data-testid="footer-social-tiktok"
                className="h-11 w-11 inline-flex items-center justify-center rounded-full border border-[#FAFAFA]/15 hover:bg-[#FAFAFA] hover:text-[#082133] transition"
              >
                <TikTokIcon className="h-4 w-4" />
              </a>
              <a
                href={SITE.social.youtube}
                target="_blank"
                rel="noreferrer"
                aria-label="YouTube"
                data-testid="footer-social-youtube"
                className="h-11 w-11 inline-flex items-center justify-center rounded-full border border-[#FAFAFA]/15 hover:bg-[#FAFAFA] hover:text-[#082133] transition"
              >
                <Youtube className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Navigate */}
          <div className="lg:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#FAFAFA]/55 font-semibold">
              {t("footer.navTitle")}
            </h4>
            <ul className="mt-5 space-y-3">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    data-testid={`footer-nav-${n.to.replace(/\//g, "") || "home"}`}
                    className="text-[15px] text-[#FAFAFA]/85 hover:text-[#FAFAFA] transition"
                  >
                    {t(n.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#FAFAFA]/55 font-semibold">
              {t("footer.servicesTitle")}
            </h4>
            <ul className="mt-5 space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={s.to}
                    data-testid={`footer-service-${s.slug}`}
                    className="text-[15px] text-[#FAFAFA]/85 hover:text-[#FAFAFA] transition"
                  >
                    {t(`serviceData.${s.key}.title`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service areas */}
          <div className="lg:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#FAFAFA]/55 font-semibold">
              {t("common.serviceAreas")}
            </h4>
            <ul className="mt-5 space-y-3">
              {SERVICE_AREAS.map((a) => (
                <li key={a.slug}>
                  <Link
                    to={`/service-areas/${a.slug}`}
                    data-testid={`footer-area-${a.slug}`}
                    className="text-[15px] text-[#FAFAFA]/85 hover:text-[#FAFAFA] transition"
                  >
                    {a.shortName[lang] || a.shortName.en}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] text-[#FAFAFA]/55 font-semibold">
              {t("footer.contactTitle")}
            </h4>
            <ul className="mt-5 space-y-4 text-[15px] text-[#FAFAFA]/85">
              <li className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-1 text-[#FAFAFA]/60" />
                <a
                  href={SITE.phoneHref}
                  data-testid="footer-call-link"
                  className="hover:text-[#FAFAFA]"
                >
                  {SITE.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-1 text-[#FAFAFA]/60" />
                <span>
                  {SITE.region}
                  <br />
                  <span className="text-[#FAFAFA]/55 text-[13px]">
                    {SITE.serviceAreas.slice(0, 3).join(" · ")} {t("common.seeAllAreas")}
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-4 w-4 mt-1 text-[#FAFAFA]/60" />
                <div className="space-y-1">
                  {SITE.hours.map((h, i) => (
                    <div key={i} className="text-[14px]">
                      <span className="text-[#FAFAFA]/65">{hourLabel(h.dayKey)}:</span>{" "}
                      <span className="text-[#FAFAFA]/90">{hourTime(h)}</span>
                    </div>
                  ))}
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#FAFAFA]/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-[13px] text-[#FAFAFA]/55">
            © {new Date().getFullYear()} {SITE.brandFull}. {t("footer.rights")}
          </p>
          <p className="text-[13px] text-[#FAFAFA]/55">{t("common.poweredBy")}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
